import { useState, useEffect, useMemo } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { type SortingState } from "@tanstack/react-table";
import { type components } from "@njbsoft/types";
import { useAccounts, type AccountListParams, type AccountSortBy } from "@/hooks/use-accounts";
import { useNeedsAttentionRollup, rollupToMap } from "@/hooks/use-needs-attention-rollup";
import { AccountsFilters, type AccountsFilterValues } from "@/components/accounts/accounts-filters";
import { useAccountsColumns } from "@/components/accounts/accounts-columns";
import { ACCOUNTS_COLUMN_REGISTRY } from "@/components/accounts/accounts-column-registry";
import { RegistryLoadError } from "@/components/custom-fields/registry-load-error";
import { BulkEditEntryButton } from "@/components/bulk-edits/entry/bulk-edit-entry-button";
import { accountListSelection } from "@/components/bulk-edits/entry/account-list-selection";
import { useSettledSelectionTotal } from "@/components/bulk-edits/entry/use-settled-selection-total";
import { useEntityColumnRegistry } from "@/hooks/use-entity-column-registry";
import { useListColumnPreferences } from "@/hooks/use-list-column-preferences";
import {
  PageHeader,
  Button,
  DataTable,
  Pagination,
  EmptyState,
  SkeletonTable,
  sortByForColumnId,
  visibleColumnCount as computeVisibleColumnCount,
} from "@/components/ui";

type Account = components["schemas"]["Account"];

export const Route = createFileRoute("/_authenticated/accounts/")({
  component: AccountsListPage,
});

const PAGE_SIZE = 20;

function AccountsListPage() {
  const { t } = useTranslation(["accounts"]);
  const navigate = useNavigate();
  const [filters, setFilters] = useState<AccountsFilterValues>({
    deleted_filter: "active",
  });
  const [offset, setOffset] = useState(0);
  const [sorting, setSorting] = useState<SortingState>([{ id: "name", desc: false }]);

  // The org's custom-field definitions append as dynamic columns; the query's
  // success gates the table and the filter bar below (a pending or failed
  // definitions query must never reach the preference-save path).
  const {
    registry: accountsRegistry,
    defsByFieldKey,
    ready: registryReady,
    isError: registryFailed,
    refetch: refetchDefinitions,
  } = useEntityColumnRegistry("account", ACCOUNTS_COLUMN_REGISTRY);

  const { visibility, setColumnVisibility, resetColumns } = useListColumnPreferences(
    "accounts",
    accountsRegistry,
  );

  useEffect(() => {
    setOffset(0);
  }, [sorting, filters]);

  const firstSort = sorting[0];
  // Read off the DYNAMIC registry: a custom column carries sortBy null, so a
  // click on its header can never produce a wire sort key the API 400s on. The
  // cast is licensed by the registry test, which pins every registry sort key
  // to the `AccountSortBy` union in both directions; `sortByForColumnId` itself
  // is typed `string | undefined`.
  const sortBy = firstSort
    ? (sortByForColumnId(accountsRegistry, firstSort.id) as AccountSortBy | undefined)
    : undefined;
  const sortDir = firstSort ? (firstSort.desc ? "desc" : "asc") : undefined;

  // The one params object feeds both the list read and the bulk edit
  // selection, so the button always describes the rows on screen.
  const listParams: AccountListParams = {
    ...filters,
    sort_by: sortBy,
    sort_dir: sortDir,
    limit: PAGE_SIZE,
    offset,
  };
  const { data, isLoading, isError, isPlaceholderData } = useAccounts(listParams);
  const selection = accountListSelection(listParams);
  const selectionTotal = useSettledSelectionTotal(selection, {
    total: data?.total ?? 0,
    isPlaceholderData,
    isError,
  });

  // Fetch the account rollup ONCE for the visible page (no N+1). The server
  // counts each assembly under its premise's flagged correspondence account;
  // the flag drills into the enforcement dashboard narrowed to the same account.
  const accountIds = useMemo(() => (data?.items ?? []).map((a) => a.id), [data]);
  const { data: rollupData } = useNeedsAttentionRollup("account", accountIds);
  const rollupMap = useMemo(() => rollupToMap(rollupData), [rollupData]);

  // Memoized: the adapter is a columns-memo dependency, so a fresh object per
  // render would rebuild every column on every render.
  const needsAttention = useMemo(
    () => ({
      rollup: rollupMap,
      onDrill: (accountId: string) =>
        void navigate({
          to: "/backflow/out-of-compliance",
          search: { account_id: accountId },
        }),
    }),
    [rollupMap, navigate],
  );
  const columns = useAccountsColumns(accountsRegistry, defsByFieldKey, needsAttention);
  const extraColumnCount = columns.length - accountsRegistry.length;
  const visibleColumnCount = computeVisibleColumnCount(
    accountsRegistry,
    visibility,
    extraColumnCount,
  );

  const accounts = data?.items ?? [];
  const total = data?.total ?? 0;
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const handleRowClick = (row: Account) => {
    void navigate({ to: "/accounts/$id", params: { id: row.id } });
  };

  if (!registryReady) {
    // Definitions unresolved: no table, no filter bar (it carries the Columns
    // picker), no preference-save path. Pending gets the standing skeleton; a
    // FAILED read gets the error and a retry, because a skeleton that never
    // resolves reads as a page still loading.
    return (
      <div className="p-8">
        <PageHeader title={t("list.title")} />
        <div className="mt-6">
          {registryFailed ? (
            <RegistryLoadError onRetry={refetchDefinitions} />
          ) : (
            <SkeletonTable columns={visibleColumnCount} rows={5} />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <PageHeader
        title={t("list.title")}
        actions={
          <>
            <BulkEditEntryButton
              recordType="accounts"
              translation={selection}
              total={selectionTotal}
            />
            <Button onClick={() => void navigate({ to: "/accounts/new" })}>
              {t("list.createCta")}
            </Button>
          </>
        }
      />

      <div className="mt-6">
        <AccountsFilters
          filters={filters}
          onChange={setFilters}
          registry={accountsRegistry}
          columnVisibility={visibility}
          onColumnVisibilityChange={setColumnVisibility}
          onColumnReset={resetColumns}
        />
      </div>

      {isError && (
        <div className="mt-6 rounded-md bg-danger/10 p-4 text-sm text-danger">
          {t("errors.loadFailed")}
        </div>
      )}

      {isLoading ? (
        <div className="mt-6">
          <SkeletonTable columns={visibleColumnCount} rows={5} />
        </div>
      ) : accounts.length === 0 ? (
        <div className="mt-6">
          <EmptyState
            title={t("list.empty")}
            actionLabel={t("list.createCta")}
            onAction={() => void navigate({ to: "/accounts/new" })}
          />
        </div>
      ) : (
        <>
          <div className="mt-6">
            <DataTable
              columns={columns}
              data={accounts}
              onRowClick={handleRowClick}
              sorting={sorting}
              onSortingChange={setSorting}
              columnVisibility={visibility}
              onColumnVisibilityChange={setColumnVisibility}
            />
          </div>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={total}
            pageSize={PAGE_SIZE}
            onPageChange={(p) => setOffset((p - 1) * PAGE_SIZE)}
          />
        </>
      )}
    </div>
  );
}
